
  import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
  import vSelect from 'vue-select';
  import debounce from 'lodash/debounce';
  import {
    Client,
    InvoiceBatchModel,
    IInvoiceBatchModel,
    IClientSearchResultModel,
    ClientSearchResultModel,
    CreateInvoiceBatchRequestModel,
    UpdateInvoiceBatchRequestModel
  } from '@/services/api.service';
  import * as log from 'loglevel';
  import * as StringUtils from '@/utilities/string.utils';
  import { Guid } from 'guid-typescript';
  import { EventBus } from '@/event-bus';
  import { InvoiceBatchCreatedMessage, InvoiceBatchUpdatedMessage } from '@/services/eventbus.models';

  @Component({
    props: {
      batch: {
        type: Object as () => IInvoiceBatchModel,
        required: false
      },
      isInDialogMode: Boolean
    },
    components: {
      vSelect
    }
  })
  export default class EditInvoiceBatch extends Vue {
    private gid: string = '';
    private batchName: string = '';
    private defaultInvoiceDate: Date = new Date();
    private useActivityDateAsInvoiceDate: boolean = false;

    private isClientBatch: boolean = false;
    private clientName: string = '';
    private selectedClient: ClientSearchResultModel | null = null;
    private matchingClients: ClientSearchResultModel[] = [];

    private isNew: boolean = false;
    private submitButtonText: string = 'Create';
    private dialogTitle: string = 'Create New Invoice Batch';

    protected mounted() {
      if (this.$props.batch) {
        this.gid = this.$props.batch.gid;
        this.batchName = this.$props.batch.batchName;
        this.useActivityDateAsInvoiceDate = this.$props.batch.useActivityDateAsInvoiceDate;
        this.defaultInvoiceDate = this.$props.batch.defaultInvoiceDate;
        this.isClientBatch = this.$props.batch.isClientBatch;
        if (this.$props.batch.isClientBatch) {
          const clientModel = new ClientSearchResultModel();
          clientModel.init({ name: this.$props.batch.clientName });
          this.selectedClient = clientModel;
        }
        this.dialogTitle = 'Edit Invoice Batch';
      } else {
        this.isNew = true;
      }
    }

    private createOrUpdateInvoiceBatch() {
      if (this.isNew) {
        this.createInvoiceBatch();
      } else {
        this.updateInvoiceBatch();
      }
    }
    private updateInvoiceBatch() {
      const model: UpdateInvoiceBatchRequestModel = new UpdateInvoiceBatchRequestModel();

      let theClientGid: string | undefined;
      if (this.isClientBatch && this.selectedClient) {
        theClientGid = this.selectedClient.gid || undefined;
      }
      model.init({
        batchName: this.batchName,
        gid: this.gid,
        openDateTimeUtc: this.$props.batch.openDateTimeUtc,
        closedDateTimeUtc: this.$props.batch.closedDateTimeUtc,
        isDeleted: this.$props.batch.isDeleted,
        defaultInvoiceDate: this.defaultInvoiceDate,
        useActivityDateAsInvoiceDate: this.useActivityDateAsInvoiceDate,
        clientGid: theClientGid
      });

      const clientService: Client = new Client(process.env.VUE_APP_API_URL, this.$http);
      // @ts-ignore
      this.$Progress.start();
      clientService
        .updateInvoiceBatch(model.gid || '', model)
        .then(batch => {
          // Schedule the item if the schedule is set
          // otherwise complete out the process
          // @ts-ignore
          this.$Progress.finish();
          // @ts-ignore
          this.$awn.success('Invoice batch ' + this.batchName + ' updated successfully');
          // Raise the created events, and publish the event on the bus
          this.$emit('invoice-batch-updated', batch);
          EventBus.$emit('invoice-batch-updated', new InvoiceBatchUpdatedMessage(batch));
          this.closeModal();
        })
        .catch(reason => {
          // @ts-ignore
          this.$Progress.fail();
          // @ts-ignore
          this.$awn.alert('Failed to update the invoice batch.\r\n' + reason.message);
        });
    }
    private createInvoiceBatch() {
      const model: CreateInvoiceBatchRequestModel = new CreateInvoiceBatchRequestModel();
      const newGid = Guid.create();
      let theClientGid: string | undefined;
      if (this.isClientBatch && this.selectedClient) {
        theClientGid = this.selectedClient.gid || undefined;
      }
      model.init({
        batchName: this.batchName,
        gid: newGid.toString(),
        openDateTimeUtc: new Date(),
        defaultInvoiceDate: this.defaultInvoiceDate,
        useActivityDateAsInvoiceDate: this.useActivityDateAsInvoiceDate,
        clientGid: theClientGid
      });

      const clientService: Client = new Client(process.env.VUE_APP_API_URL, this.$http);
      // @ts-ignore
      this.$Progress.start();
      clientService
        .createInvoiceBatch( model)
        .then((batch: IInvoiceBatchModel) => {
          // Schedule the item if the schedule is set
          // otherwise complete out the process
          // @ts-ignore
          this.$Progress.finish();
          // @ts-ignore
          this.$awn.success('Invoice batch ' + this.batchName + ' created successfully');
          // Raise the created events, and publish the event on the bus
          this.$emit('invoice-batch-created', new InvoiceBatchCreatedMessage(batch));
          EventBus.$emit('invoice-batch-created', new InvoiceBatchCreatedMessage(batch));
          this.closeModal();
        })
        .catch((reason: any) => {
          // @ts-ignore
          this.$Progress.fail();
          // @ts-ignore
          this.$awn.alert('Failed to create the invoice batch.\r\n' + reason.message);
        });
    }

    private searchForClients(search: string, loading: (show: boolean) => any) {
      if (search !== undefined && search !== null && search !== '') {
        loading(true);
        this.internalSearchForClients(search, loading, this);
      }
    }

    // Taken from: https://stackoverflow.com/questions/40686767/using-lodash-debounce-in-angular2-typescript-scroll-not-working
    private internalSearchForClients = debounce((search, loading, vm) => {
      const clientService = new Client(process.env.VUE_APP_API_URL, this.$http);
      clientService.searchClients(search).then(data => {
        // push the results into the list
        vm.matchingClients = data.sort((n1, n2) => StringUtils.stringCompare(n1.name || '', n2.name || ''));
        loading(false);
      });
    }, 300);

    @Watch('isNew')
    private onIsNewChanged() {
      if (this.isNew) {
        this.submitButtonText = 'Create';
      } else {
        this.submitButtonText = 'Update';
      }
    }

    private cancelEdit() {
      this.closeModal();
    }

    private closeModal() {
      if (this.$props.isInDialogMode) {
        this.$modal.hide('modal-invoicebatch-edit');
      }
    }
  }
